import type { ASTFileParsed, ASTRow } from '../../lib/parseASTDiff'
import type { ASTChange } from '../../lib/astDiffTypes'
import type { FileDiffParsed, DiffRowParsed } from '../../lib/parseDiff'
import type { Finding } from '../../gen/types'

type FindingMap = Map<string, Finding[]>
type CommentMap = Map<string, { side: 'left' | 'right'; replies: string[]; open: boolean }>

type RenderInlineCards = (
  leftKey: string,
  rightKey: string,
  leftFindings: Finding[],
  rightFindings: Finding[],
) => React.ReactNode

interface ASTDiffViewerProps {
  astFile: ASTFileParsed
  unifiedFile: FileDiffParsed
  fileIdx: number
  reviewId: string
  findingMap: FindingMap
  commentMap: CommentMap
  openCommentKey: string | null
  onLineClick: (path: string, side: 'left' | 'right', lineNum: number) => void
  baseLabel: string
  headLabel: string
  renderInlineCards: RenderInlineCards
  renderUnifiedFallback: () => React.ReactNode
}

function buildASTMaps(rows: ASTRow[]): {
  leftMap: Map<number, ASTChange[]>
  rightMap: Map<number, ASTChange[]>
} {
  const leftMap = new Map<number, ASTChange[]>()
  const rightMap = new Map<number, ASTChange[]>()
  for (const row of rows) {
    if (row.kind === 'del') {
      leftMap.set(row.lhsLine, row.lhsChanges)
    } else if (row.kind === 'add') {
      rightMap.set(row.rhsLine, row.rhsChanges)
    } else if (row.kind === 'pair') {
      leftMap.set(row.lhsLine, row.lhsChanges)
      rightMap.set(row.rhsLine, row.rhsChanges)
    }
  }
  return { leftMap, rightMap }
}

// Whole-line coverage check: when every non-ws character is part of a changed
// token, per-token highlights add noise — the line tint already says "changed".
function coversEntireLine(content: string, changes: ASTChange[]): boolean {
  if (changes.length === 0) return false
  const totalNonWs = content.replace(/\s/g, '').length
  if (totalNonWs === 0) return true
  let pos = 0
  let uncoveredNonWs = 0
  for (const ch of changes) {
    if (ch.start > pos) uncoveredNonWs += content.slice(pos, ch.start).replace(/\s/g, '').length
    if (ch.end > pos) pos = ch.end
  }
  uncoveredNonWs += content.slice(pos).replace(/\s/g, '').length
  return uncoveredNonWs / totalNonWs < 0.15
}

function renderLineContent(
  content: string,
  changes: ASTChange[] | undefined,
  isAdded: boolean,
): React.ReactNode {
  if (!changes || changes.length === 0 || coversEntireLine(content, changes)) {
    return <span className="line-content">{content}</span>
  }
  const tokenClass = isAdded ? 'tk-add' : 'tk-del'
  const spans: React.ReactNode[] = []
  let pos = 0
  for (let i = 0; i < changes.length; i++) {
    const ch = changes[i]
    if (ch.start > pos) {
      spans.push(<span key={`u${pos}`}>{content.slice(pos, ch.start)}</span>)
    }
    spans.push(
      <mark key={`c${ch.start}`} className={tokenClass}>
        {ch.content}
      </mark>,
    )
    pos = ch.end
  }
  if (pos < content.length) {
    spans.push(<span key="tail">{content.slice(pos)}</span>)
  }
  return <span className="line-content">{spans}</span>
}

interface RowRendererProps {
  row: DiffRowParsed
  path: string
  leftMap: Map<number, ASTChange[]>
  rightMap: Map<number, ASTChange[]>
  findingMap: FindingMap
  commentMap: CommentMap
  openCommentKey: string | null
  onLineClick: (path: string, side: 'left' | 'right', lineNum: number) => void
  renderInlineCards: RenderInlineCards
}

function RowRenderer({
  row, path, leftMap, rightMap, findingMap, commentMap, openCommentKey, onLineClick, renderInlineCards,
}: RowRendererProps) {
  if (row.kind === 'hunk-header') {
    return <div className="hunk-header">{row.text}</div>
  }

  if (row.kind === 'context') {
    return (
      <>
        <div className="diff-line ctx side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num">{row.leftNum}</span>
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content">{row.content}</span>
        </div>
        <div className="diff-line ctx" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num">{row.rightNum}</span>
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content">{row.content}</span>
        </div>
      </>
    )
  }

  if (row.kind === 'del') {
    const leftKey = `${path}:left:${row.leftNum}`
    const leftFindings = findingMap.get(`${path}:${row.leftNum}`) ?? []
    const hasLeft = leftKey === openCommentKey || commentMap.has(leftKey) || leftFindings.length > 0

    return (
      <>
        <div className="diff-line del side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'left', row.leftNum)}>
            {row.leftNum}
          </span>
          <span
            className="line-gutter clickable"
            onClick={() => onLineClick(path, 'left', row.leftNum)}
          />
          <span className="line-marker">−</span>
          {renderLineContent(row.content, leftMap.get(row.leftNum), false)}
        </div>
        <div className="diff-line empty">
          <span className="line-num" />
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content" />
        </div>
        {hasLeft && renderInlineCards(leftKey, `${path}:right:0`, leftFindings, [])}
      </>
    )
  }

  if (row.kind === 'add') {
    const rightKey = `${path}:right:${row.rightNum}`
    const rightFindings = findingMap.get(`${path}:${row.rightNum}`) ?? []
    const hasRight = rightKey === openCommentKey || commentMap.has(rightKey) || rightFindings.length > 0

    return (
      <>
        <div className="diff-line empty side-l">
          <span className="line-num" />
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content" />
        </div>
        <div className="diff-line add" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'right', row.rightNum)}>
            {row.rightNum}
          </span>
          <span
            className="line-gutter clickable"
            onClick={() => onLineClick(path, 'right', row.rightNum)}
          />
          <span className="line-marker">+</span>
          {renderLineContent(row.content, rightMap.get(row.rightNum), true)}
        </div>
        {hasRight && renderInlineCards(`${path}:left:0`, rightKey, [], rightFindings)}
      </>
    )
  }

  if (row.kind === 'pair') {
    const leftKey = `${path}:left:${row.leftNum}`
    const rightKey = `${path}:right:${row.rightNum}`
    const rightFindings = findingMap.get(`${path}:${row.rightNum}`) ?? []
    const hasLeft = leftKey === openCommentKey || commentMap.has(leftKey)
    const hasRight = rightKey === openCommentKey || commentMap.has(rightKey) || rightFindings.length > 0

    return (
      <>
        <div className="diff-line del side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'left', row.leftNum)}>
            {row.leftNum}
          </span>
          <span
            className="line-gutter clickable"
            onClick={() => onLineClick(path, 'left', row.leftNum)}
          />
          <span className="line-marker">−</span>
          {renderLineContent(row.leftContent, leftMap.get(row.leftNum), false)}
        </div>
        <div className="diff-line add" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'right', row.rightNum)}>
            {row.rightNum}
          </span>
          <span
            className="line-gutter clickable"
            onClick={() => onLineClick(path, 'right', row.rightNum)}
          />
          <span className="line-marker">+</span>
          {renderLineContent(row.rightContent, rightMap.get(row.rightNum), true)}
        </div>
        {(hasLeft || hasRight) && renderInlineCards(leftKey, rightKey, [], rightFindings)}
      </>
    )
  }

  return null
}

export function ASTDiffViewer({
  astFile, unifiedFile, fileIdx, findingMap, commentMap, openCommentKey,
  onLineClick, baseLabel, headLabel, renderInlineCards, renderUnifiedFallback,
}: ASTDiffViewerProps) {
  const path = astFile.path
  const pathName = path.split('/').pop() ?? path
  const pathDir = path.includes('/') ? path.slice(0, path.lastIndexOf('/') + 1) : ''

  const { leftMap, rightMap } = buildASTMaps(astFile.rows)

  let fileFindings = 0
  findingMap.forEach((fs, key) => {
    if (key.startsWith(`${path}:`)) fileFindings += fs.length
  })

  return (
    <div className="border-b-2 border-border-default" id={`file-${fileIdx}`}>
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-1.5 bg-surface border-b border-border-default">
        <span className="font-mono text-[13px] font-bold text-link flex-1 min-w-0 truncate">
          <span className="text-fg-dim font-normal">{pathDir}</span>
          {pathName}
          {fileFindings > 0 && (
            <span className="ml-2 text-[10px] text-sev-blocking font-normal">
              ⚑ {fileFindings} finding{fileFindings !== 1 ? 's' : ''}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          {astFile.language && (
            <span className="text-[10px] text-fg-dim font-mono border border-border-subtle rounded-[3px] px-1.5 py-[1px] uppercase">
              {astFile.language}
            </span>
          )}
          <span className="text-[10px] text-accent font-mono border border-accent-dim bg-accent-bg rounded-[3px] px-1.5 py-[1px] uppercase tracking-[0.06em]">
            AST
          </span>
        </span>
      </div>

      {astFile.error ? (
        <>
          <div className="px-4 py-2 bg-sev-blocking/10 border-b border-sev-blocking/30 text-sev-blocking text-[11px] font-mono">
            ⚠ AST diff unavailable: {astFile.error}
          </div>
          {renderUnifiedFallback()}
        </>
      ) : (
        <div className="diff-split">
          <div className="diff-side-header side-l">
            <span className="side-label">base</span>
            <span className="side-sha">{baseLabel}</span>
          </div>
          <div className="diff-side-header">
            <span className="side-label">head</span>
            <span className="side-sha">{headLabel}</span>
          </div>

          {unifiedFile.rows.map((row, rowIdx) => (
            <RowRenderer
              key={rowIdx}
              row={row}
              path={path}
              leftMap={leftMap}
              rightMap={rightMap}
              findingMap={findingMap}
              commentMap={commentMap}
              openCommentKey={openCommentKey}
              onLineClick={onLineClick}
              renderInlineCards={renderInlineCards}
            />
          ))}
        </div>
      )}
    </div>
  )
}
